import { FilterData } from 'components/RadioGroup/Filters'
import { Discovery, EvaluationTask, Maybe, Scalars } from 'generated/graphql'
import { compact, filter, find, map, uniq } from 'lodash'
import { TotalDiscovery } from 'pages/Evaluation/state/useEvaluationPageState'
import { useCallback, useState } from 'react'

export const useFilteredDiscoveries = (
  task: EvaluationTask,
  discoveries: TotalDiscovery[]
): {
  filtersData: FilterData[]
  setFiltersData: (data: FilterData[]) => void
  getFilteredDiscoveries: () => Discovery[]
} => {
  const [filtersData, setFiltersData] = useState<FilterData[]>(
    getInitialFiltersData(task, discoveries)
  )

  const getFilteredDiscoveries = useCallback(() => {
    return calculateFilteredDiscoveries(filtersData, task, discoveries)
  }, [filtersData, task, discoveries])

  return { filtersData, setFiltersData, getFilteredDiscoveries }
}

export enum FilterCategory {
  RATINGS = 'Ratings',
}

const getDiscoveryRatingOptionName = (
  task: EvaluationTask,
  ratingOptionId?: Maybe<Scalars['ID']>
) => {
  return task.factor.discoveryRatingOptions.find(
    (ratingOption) => ratingOption.id === ratingOptionId
  )?.name
}

const getInitialFiltersData = (
  task: EvaluationTask,
  discoveries: TotalDiscovery[]
): FilterData[] => {
  return [
    {
      category: FilterCategory.RATINGS,
      options: map(
        uniq(
          compact(
            map(discoveries, (discovery) =>
              getDiscoveryRatingOptionName(task, discovery.rating.option)
            )
          )
        ),
        (ratingOptionName) => ({
          title: ratingOptionName,
          selected: false,
        })
      ),
    },
  ]
}

export const calculateFilteredDiscoveries = (
  filters: FilterData[],
  task: EvaluationTask,
  discoveries: TotalDiscovery[]
): Discovery[] => {
  let filteredDiscoveries: Discovery[] = discoveries

  const ratingOptions = map(
    filter(
      find(filters, (filter) => filter.category === FilterCategory.RATINGS)
        ?.options,
      (option) => option.selected
    ),
    (option) => option.title
  )

  if (ratingOptions.length > 0) {
    filteredDiscoveries = filter(
      discoveries,
      (discovery) =>
        !!discovery.rating.option &&
        ratingOptions.includes(
          getDiscoveryRatingOptionName(task, discovery.rating.option) || ''
        )
    )
  }

  return filteredDiscoveries
}
